import { DateTime } from "luxon";
import { getActivityTitle, isActivityType } from "@/lib/activities/registry";
import type { ActivityType } from "@/lib/api/activities";
import type {
  MentorshipDetails,
  NoticeOpportunityActivity,
} from "@/lib/api/mentorships";
import type { HubView } from "@/components/hub/use-hub-store";
import {
  hubViewForActivityType,
  hubViewForContentEntityType,
} from "@/components/hub/hub-view-for-entity";
import { getPreferredName } from "@/lib/redacted-display";
import { resolveTeenBirthdaySignal } from "../../notice/teen-birthday";

export const ACTION_SUGGESTION_TIMEZONE = "America/New_York";

export type ActionSuggestionKind =
  | "birthday"
  | "activity_finished"
  | "hub_content_shared"
  | "opportunity_hot_takes"
  | "opportunity_connect"
  | "opportunity_deep_thoughts"
  | "check_in"
  | "opportunity_swap"
  | "risk_of_pause"
  | "end_of_day"
  | "empty";

export type ActionSuggestionOpenActionId = "open_activity" | "open_hub";

export type ActionSuggestionLaunchActionId =
  | "share_moment"
  | "hot_takes"
  | "connect_moment"
  | "deep_thoughts"
  | "start_swap";

export interface ActionSuggestionTargetEntity {
  id: string;
  view: HubView;
}

type ActionSuggestionBase = {
  kind: ActionSuggestionKind;
  eyebrow: "where_id_start" | "how_to_end";
  rationale: string;
  dismissible: boolean;
};

export type ActionSuggestionDescriptor =
  | (ActionSuggestionBase & {
      primaryAction: null;
    })
  | (ActionSuggestionBase & {
      primaryAction: {
        id: ActionSuggestionOpenActionId;
        label: string;
        targetEntity: ActionSuggestionTargetEntity;
      };
    })
  | (ActionSuggestionBase & {
      primaryAction: { id: ActionSuggestionLaunchActionId; label: string };
    });

export interface ActionSuggestionSignals {
  teenName: string;
  birthday: boolean;
  birthdayWeekendDay: "Saturday" | "Sunday" | null;
  activityPending: boolean;
  activityType: ActivityType | null;
  activityEntityId: string | null;
  hubContentPending: boolean;
  hubContentType: "moment" | "note" | null;
  hubContentEntityId: string | null;
  opportunityActivity: NoticeOpportunityActivity | null;
  checkInDue: boolean;
  opportunityHub: boolean;
  riskOfPause: boolean;
  endOfDay: boolean;
}

interface KindRule {
  kind: ActionSuggestionKind;
  match: (signals: ActionSuggestionSignals) => boolean;
  resolve: (signals: ActionSuggestionSignals) => ActionSuggestionDescriptor;
}

const START_SWAP_ACTION = {
  id: "start_swap" as const,
  label: "Start a Swap",
};

const OPPORTUNITY_BY_ACTIVITY: Record<
  NoticeOpportunityActivity,
  {
    kind: Extract<
      ActionSuggestionKind,
      | "opportunity_hot_takes"
      | "opportunity_connect"
      | "opportunity_deep_thoughts"
    >;
    action: {
      id: Extract<
        ActionSuggestionLaunchActionId,
        "hot_takes" | "connect_moment" | "deep_thoughts"
      >;
      label: string;
    };
    rationale: (signals: ActionSuggestionSignals) => string;
  }
> = {
  hot_takes: {
    kind: "opportunity_hot_takes",
    action: { id: "hot_takes", label: "Send Hot Takes" },
    rationale: () =>
      "Hot Takes is a low stakes way to spark conversation today!",
  },
  connect_moment: {
    kind: "opportunity_connect",
    action: { id: "connect_moment", label: "Send Connection Cards" },
    rationale: (s) =>
      `Connection Cards is a great way to get closer with ${s.teenName} today!`,
  },
  deep_thoughts: {
    kind: "opportunity_deep_thoughts",
    action: { id: "deep_thoughts", label: "Send Deep Thoughts" },
    rationale: (s) =>
      `${s.teenName} might be ready to share a little more - try out Deep Thoughts today!`,
  },
};

export function opportunityActionKind(
  activity: NoticeOpportunityActivity,
): ActionSuggestionKind {
  return OPPORTUNITY_BY_ACTIVITY[activity].kind;
}

export function resolveActionTeenDisplayName(
  mentorship: MentorshipDetails,
): string {
  return getPreferredName(mentorship.mentee) || "your teen";
}

function opportunityRule(activity: NoticeOpportunityActivity): KindRule {
  const config = OPPORTUNITY_BY_ACTIVITY[activity];
  return {
    kind: config.kind,
    match: (s) => s.opportunityActivity === activity,
    resolve: (s) => ({
      kind: config.kind,
      eyebrow: "where_id_start",
      rationale: config.rationale(s),
      primaryAction: config.action,
      dismissible: true,
    }),
  };
}

function startSwapRule(
  kind: Extract<
    ActionSuggestionKind,
    "check_in" | "opportunity_swap" | "risk_of_pause"
  >,
  match: (signals: ActionSuggestionSignals) => boolean,
  rationale: (signals: ActionSuggestionSignals) => string,
): KindRule {
  return {
    kind,
    match,
    resolve: (s) => ({
      kind,
      eyebrow: "where_id_start",
      rationale: rationale(s),
      primaryAction: START_SWAP_ACTION,
      dismissible: true,
    }),
  };
}

export function isEndOfDayWindow(now: DateTime = DateTime.now()): boolean {
  const local = now.setZone(ACTION_SUGGESTION_TIMEZONE);
  const minutes = local.hour * 60 + local.minute;
  const weekday = local.weekday;

  if (weekday >= 1 && weekday <= 4) {
    return minutes >= 21 * 60 + 45 && minutes < 22 * 60 + 5;
  }
  if (weekday === 5) {
    return minutes >= 18 * 60 + 45 && minutes < 19 * 60 + 5;
  }
  return false;
}

export function buildActionSuggestionSignals(
  mentorship: MentorshipDetails,
  now: DateTime = DateTime.now(),
): ActionSuggestionSignals {
  const localNow = now.setZone(ACTION_SUGGESTION_TIMEZONE);
  const notice = mentorship.notice_signals;
  const pendingActivity = notice?.pending_activity ?? null;
  const pendingHub = notice?.pending_hub_content ?? null;
  const activityType =
    pendingActivity && isActivityType(pendingActivity.activity_type)
      ? pendingActivity.activity_type
      : null;
  const birthday = resolveTeenBirthdaySignal(
    mentorship.mentee?.birthday_md,
    localNow,
  );

  return {
    teenName: resolveActionTeenDisplayName(mentorship),
    birthday: birthday.isToday && !(notice?.mentor_messaged_today ?? false),
    birthdayWeekendDay: birthday.weekendDay,
    activityPending: Boolean(
      notice?.activity_pending && activityType && pendingActivity?.entity_id,
    ),
    activityType,
    activityEntityId: pendingActivity?.entity_id ?? null,
    hubContentPending: Boolean(
      notice?.hub_content_pending && pendingHub?.entity_id,
    ),
    hubContentType: pendingHub?.entity_type ?? null,
    hubContentEntityId: pendingHub?.entity_id ?? null,
    opportunityActivity: notice?.opportunity_activity ?? null,
    checkInDue: notice?.check_in_due ?? false,
    opportunityHub: notice?.opportunity_hub ?? false,
    riskOfPause: notice?.risk_of_pause ?? false,
    endOfDay: isEndOfDayWindow(localNow),
  };
}

const KIND_RULES: readonly KindRule[] = [
  {
    kind: "birthday",
    match: (s) => s.birthday,
    resolve: (s) => ({
      kind: "birthday",
      eyebrow: "where_id_start",
      rationale: s.birthdayWeekendDay
        ? `It was ${s.teenName}'s birthday on ${s.birthdayWeekendDay}! Send them something special today!`
        : `It's ${s.teenName}'s birthday! Send them something special today!`,
      primaryAction: { id: "share_moment", label: "Share a Moment" },
      dismissible: true,
    }),
  },
  {
    kind: "activity_finished",
    match: (s) => s.activityPending,
    resolve: (s) => {
      const view = s.activityType
        ? hubViewForActivityType(s.activityType)
        : null;
      if (!s.activityEntityId || !view) {
        return {
          kind: "activity_finished",
          eyebrow: "where_id_start",
          rationale: `${s.teenName} just finished ${
            s.activityType ? getActivityTitle(s.activityType) : "an activity"
          }. Check it out!`,
          primaryAction: null,
          dismissible: true,
        };
      }
      return {
        kind: "activity_finished",
        eyebrow: "where_id_start",
        rationale: `${s.teenName} just finished ${
          s.activityType ? getActivityTitle(s.activityType) : "an activity"
        }. Check it out!`,
        primaryAction: {
          id: "open_activity",
          label: "Check it out",
          targetEntity: { id: s.activityEntityId, view },
        },
        dismissible: true,
      };
    },
  },
  {
    kind: "hub_content_shared",
    match: (s) => s.hubContentPending,
    resolve: (s) => {
      const view = s.hubContentType
        ? hubViewForContentEntityType(s.hubContentType)
        : null;
      if (!s.hubContentEntityId || !view) {
        return {
          kind: "hub_content_shared",
          eyebrow: "where_id_start",
          rationale: `${s.teenName} just shared Content in the Hub. Check it out!`,
          primaryAction: null,
          dismissible: true,
        };
      }
      return {
        kind: "hub_content_shared",
        eyebrow: "where_id_start",
        rationale: `${s.teenName} just shared Content in the Hub. Check it out!`,
        primaryAction: {
          id: "open_hub",
          label: "Check it out",
          targetEntity: { id: s.hubContentEntityId, view },
        },
        dismissible: true,
      };
    },
  },
  startSwapRule(
    "check_in",
    (s) => s.checkInDue,
    (s) =>
      `Check in with ${s.teenName} today! Send a personal message or start an activity.`,
  ),
  startSwapRule(
    "risk_of_pause",
    (s) => s.riskOfPause,
    (s) =>
      `${s.teenName} is at risk of being paused. Try something different today - there's no wrong answers!`,
  ),
  opportunityRule("hot_takes"),
  opportunityRule("connect_moment"),
  opportunityRule("deep_thoughts"),
  startSwapRule(
    "opportunity_swap",
    (s) => s.opportunityHub,
    (s) =>
      `Try out a Swap with ${s.teenName} today - it's a great way to build connection!`,
  ),
  {
    kind: "end_of_day",
    match: (s) => s.endOfDay,
    resolve: () => ({
      kind: "end_of_day",
      eyebrow: "how_to_end",
      rationale:
        "Send something super thoughtful to close the day out - this is important to get them to come back!",
      primaryAction: null,
      dismissible: true,
    }),
  },
  {
    kind: "empty",
    match: () => true,
    resolve: () => ({
      kind: "empty",
      eyebrow: "where_id_start",
      rationale: `Nothing surfacing today — you know your mentee best!`,
      primaryAction: null,
      dismissible: false,
    }),
  },
];

export function resolveActionSuggestion(
  signals: ActionSuggestionSignals,
  dismissedKinds: ReadonlySet<ActionSuggestionKind> = new Set(),
): ActionSuggestionDescriptor {
  for (const rule of KIND_RULES) {
    if (rule.kind !== "empty" && dismissedKinds.has(rule.kind)) continue;
    if (!rule.match(signals)) continue;
    return rule.resolve(signals);
  }
  return KIND_RULES[KIND_RULES.length - 1]!.resolve(signals);
}

export function actionSuggestionCalendarDayKey(
  now: DateTime = DateTime.now(),
): string {
  return now.setZone(ACTION_SUGGESTION_TIMEZONE).toISODate() ?? "";
}

export const ACTION_SUGGESTION_KINDS: readonly ActionSuggestionKind[] =
  KIND_RULES.map((rule) => rule.kind);
